import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button
} from '@mui/material';
import {
  ExpandMore,
  Lightbulb,
  Palette,
  Visibility,
  Speed,
  Settings
} from '@mui/icons-material';
import { useStorefront3DStore } from '../store/useStorefront3DStore';

const SettingsPanel = () => {
  const { 
    viewerSettings, 
    updateViewerSettings,
    resetToDefaults 
  } = useStorefront3DStore();

  const handleSettingChange = (key, value) => {
    updateViewerSettings({
      ...viewerSettings,
      [key]: value
    });
  };

  const environments = [
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'studio', label: 'Studio' },
    { value: 'sunset', label: 'Sunset' },
    { value: 'dawn', label: 'Dawn' },
    { value: 'night', label: 'Night' },
    { value: 'forest', label: 'Forest' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'city', label: 'City' }
  ];

  const qualityPresets = [
    { value: 'low', label: 'Low (Better Performance)' },
    { value: 'medium', label: 'Medium (Balanced)' },
    { value: 'high', label: 'High (Better Quality)' },
    { value: 'ultra', label: 'Ultra (Best Quality)' }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Viewer Settings
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        {/* Environment Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Palette />
              <Typography variant="subtitle2">Environment</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ space: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Environment Preset</InputLabel>
                <Select
                  value={viewerSettings.environment || 'warehouse'}
                  label="Environment Preset"
                  onChange={(e) => handleSettingChange('environment', e.target.value)}
                >
                  {environments.map((env) => (
                    <MenuItem key={env.value} value={env.value}>
                      {env.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" gutterBottom>
                Environment Intensity
              </Typography>
              <Slider
                value={viewerSettings.environmentIntensity || 1}
                onChange={(e, value) => handleSettingChange('environmentIntensity', value)}
                min={0}
                max={2}
                step={0.1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Lighting Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lightbulb />
              <Typography variant="subtitle2">Lighting</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ space: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={viewerSettings.shadows || true}
                    onChange={(e) => handleSettingChange('shadows', e.target.checked)}
                  />
                }
                label="Enable Shadows"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" gutterBottom>
                Ambient Light Intensity
              </Typography>
              <Slider
                value={viewerSettings.ambientIntensity || 0.4}
                onChange={(e, value) => handleSettingChange('ambientIntensity', value)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" gutterBottom>
                Directional Light Intensity
              </Typography>
              <Slider
                value={viewerSettings.directionalIntensity || 1}
                onChange={(e, value) => handleSettingChange('directionalIntensity', value)}
                min={0}
                max={2}
                step={0.1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Display Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility />
              <Typography variant="subtitle2">Display</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ space: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={viewerSettings.showGrid || true}
                    onChange={(e) => handleSettingChange('showGrid', e.target.checked)}
                  />
                }
                label="Show Grid"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={viewerSettings.showLabels || true}
                    onChange={(e) => handleSettingChange('showLabels', e.target.checked)}
                  />
                }
                label="Show Item Labels"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={viewerSettings.showPrices || false}
                    onChange={(e) => handleSettingChange('showPrices', e.target.checked)}
                  />
                }
                label="Show Prices"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" gutterBottom>
                Label Size
              </Typography>
              <Slider
                value={viewerSettings.labelSize || 1}
                onChange={(e, value) => handleSettingChange('labelSize', value)}
                min={0.5}
                max={2}
                step={0.1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Performance Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed />
              <Typography variant="subtitle2">Performance</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ space: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Quality Preset</InputLabel>
                <Select
                  value={viewerSettings.quality || 'medium'}
                  label="Quality Preset"
                  onChange={(e) => handleSettingChange('quality', e.target.value)}
                >
                  {qualityPresets.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={viewerSettings.antialiasing || true}
                    onChange={(e) => handleSettingChange('antialiasing', e.target.checked)}
                  />
                }
                label="Anti-aliasing"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" gutterBottom>
                Render Distance
              </Typography>
              <Slider
                value={viewerSettings.renderDistance || 100}
                onChange={(e, value) => handleSettingChange('renderDistance', value)}
                min={50}
                max={500}
                step={25}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Reset Settings */}
        <Card sx={{ mt: 2, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Reset Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Restore all settings to their default values
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Settings />}
              onClick={resetToDefaults}
              fullWidth
            >
              Reset to Defaults
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SettingsPanel;
